import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import CategoriesGrid from "@/components/listings/CategoriesGrid";
import ListingCard from "@/components/listings/ListingCard";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Rocket, Loader2 } from "lucide-react";
import { fetchListings } from "@/lib/api";

export default function Home() {
  const { categories } = useApp();
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchListings({ featured: 1, limit: 8, sort: "newest" }),
      fetchListings({ limit: 12, sort: "newest" }),
    ]).then(([f, l]) => {
      setFeatured(f.items || []);
      setLatest(l.items || []);
      setTotalActive(l.total || 0);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="fade-in">
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #000 0%, #1a0508 100%)" }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(237,28,36,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(237,28,36,0.15), transparent 55%)"
        }} />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full border" style={{ borderColor: "var(--amd-primary)", color: "var(--amd-primary)" }}>
              <Sparkles className="w-3 h-3" /> Marketplace românesc
            </div>
            <h1 className="mt-4 text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
              Găsește orice. <br />
              <span style={{ color: "var(--amd-primary)" }}>Vinde rapid.</span>
            </h1>
            <p className="mt-4 text-base md:text-lg max-w-xl" style={{ color: "var(--amd-text-muted)" }}>
              Mii de anunțuri verificate, din toată România. Postări gratuite, contact direct cu vânzătorul.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/adauga-anunt" className="amd-btn-primary px-6 h-12 rounded-md font-semibold inline-flex items-center gap-2">
                <Rocket className="w-4 h-4" /> Postează anunț gratuit
              </Link>
              <Link to="/cauta" className="px-6 h-12 rounded-md font-semibold inline-flex items-center gap-2 border-2 text-white hover:bg-white/5 transition-colors" style={{ borderColor: "#444" }}>
                Vezi toate anunțurile <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat icon={Zap} value={totalActive.toLocaleString("ro-RO")} label="Anunțuri active" />
              <Stat icon={ShieldCheck} value={(categories.length || 0).toString()} label="Categorii" />
              <Stat icon={Sparkles} value="24/7" label="Online" />
            </div>
          </div>
        </div>
      </section>

      <CategoriesGrid />

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div>
      ) : (
        <>
          {featured.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--amd-primary)" }}>Promovate</div>
                  <h2 className="text-xl md:text-2xl font-black text-white">Anunțuri recomandate</h2>
                </div>
                <Link to="/cauta?featured=1" className="text-sm font-semibold inline-flex items-center gap-1 amd-link">Vezi toate <ArrowRight className="w-4 h-4" /></Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {featured.map((l) => <ListingCard key={l.id} listing={l} />)}
              </div>
            </section>
          )}

          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--amd-text-muted)" }}>Proaspete</div>
                <h2 className="text-xl md:text-2xl font-black text-white">Cele mai noi anunțuri</h2>
              </div>
              <Link to="/cauta" className="text-sm font-semibold inline-flex items-center gap-1 amd-link">Vezi toate <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {latest.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="amd-card rounded-lg p-3">
      <Icon className="w-4 h-4 mb-1" style={{ color: "var(--amd-primary)" }} />
      <div className="font-black text-white text-lg leading-none">{value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--amd-text-muted)" }}>{label}</div>
    </div>
  );
}
